import { useQuery } from "@tanstack/react-query"

import type { SegmentAnalysisLoadStatus } from "../types"
import type { UseQueryResult } from "@tanstack/react-query"

import {
  fetchSegmentAnalysisPending,
  fetchSegmentAnalysisPortfolio,
} from "../api/segment-analysis-api"

/**
 * Stable react-query key factory for the Segment Analysis module. Keeping the
 * keys in one place guarantees the read queries and the mutation invalidations
 * reference byte-identical tuples (so `invalidateQueries` always hits).
 */
export const segmentAnalysisKeys = {
  all: ["segment-analysis"] as const,
  portfolio: () => [...segmentAnalysisKeys.all, "portfolio"] as const,
  pending: (segmentId: string) => [...segmentAnalysisKeys.all, "pending", segmentId] as const,
}

/**
 * Live-backend portfolio read. Disabled in mock mode so no network request
 * fires (req 2). Background-refetches on remount thanks to react-query caching.
 */
export function useSegmentAnalysisPortfolioQuery(enabled: boolean) {
  return useQuery({
    enabled,
    queryFn: fetchSegmentAnalysisPortfolio,
    queryKey: segmentAnalysisKeys.portfolio(),
  })
}

/**
 * Pending-changes read for the selected segment. Disabled unless we have a
 * segment id and we're on the live-backend path.
 */
export function useSegmentAnalysisPendingQuery(segmentId: string | null, enabled: boolean) {
  return useQuery({
    enabled: enabled && Boolean(segmentId),
    queryFn: () => fetchSegmentAnalysisPending(segmentId as string),
    queryKey: segmentAnalysisKeys.pending(segmentId ?? "__none__"),
  })
}

/**
 * Derive the legacy idle/loading/error/ready status from a react-query result.
 *
 * react-query v5 reports a *disabled* query as `status: "pending"` +
 * `fetchStatus: "idle"`; we must NOT surface that as "loading" (mock mode would
 * otherwise look perpetually loading). Map a non-fetching, data-less query to
 * "idle" to preserve the old hand-rolled semantics.
 */
export function deriveBackendStatus(
  query: Pick<UseQueryResult, "isError" | "isSuccess" | "fetchStatus" | "data">
): SegmentAnalysisLoadStatus {
  if (query.isError) return "error"
  if (query.isSuccess) return "ready"
  if (query.fetchStatus === "idle" && query.data === undefined) return "idle"
  return "loading"
}
